import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MessageSquareText, Plus, Pencil, Trash2, X, Save, Loader2 } from 'lucide-react';
import AdminLayout from '../components/AdminLayout';
import { apiFetch } from '../lib/api';

interface CsTemplate {
  id: number;
  title: string;
  body: string;
  category: string;
  use_count: number;
  is_active: number;
}

const EMPTY = { title: '', body: '', category: '通用' };

const AdminCsTemplates: React.FC = () => {
  const navigate = useNavigate();
  const [items, setItems] = useState<CsTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState<CsTemplate | null>(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const fetchItems = async () => {
    setLoading(true);
    try {
      const res = await apiFetch('/api/public/admin/cs-templates');
      if (res.status === 401) { localStorage.removeItem('admin_token'); navigate('/admin'); return; }
      const json = await res.json();
      setItems((json.data ?? json).templates || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!localStorage.getItem('admin_token')) { navigate('/admin'); return; }
    fetchItems();
  }, []);

  const handleLogout = () => {
    if (confirm('確定要登出嗎？')) { localStorage.removeItem('admin_token'); navigate('/admin'); }
  };

  const openCreate = () => { setEditing(null); setForm(EMPTY); setErr(null); setShowModal(true); };
  const openEdit = (t: CsTemplate) => {
    setEditing(t);
    setForm({ title: t.title, body: t.body, category: t.category });
    setErr(null);
    setShowModal(true);
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.title.trim() || !form.body.trim()) { setErr('標題同內容必填'); return; }
    setSaving(true);
    setErr(null);
    try {
      const res = editing
        ? await apiFetch(`/api/public/admin/cs-templates/${editing.id}`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(form) })
        : await apiFetch('/api/public/admin/cs-templates', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(form) });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || '儲存失敗');
      }
      setShowModal(false);
      await fetchItems();
    } catch (e) {
      setErr(e instanceof Error ? e.message : '儲存失敗');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (t: CsTemplate) => {
    if (!confirm(`刪除話術「${t.title}」？`)) return;
    await apiFetch(`/api/public/admin/cs-templates/${t.id}`, { method: 'DELETE' });
    await fetchItems();
  };

  const toggleActive = async (t: CsTemplate) => {
    await apiFetch(`/api/public/admin/cs-templates/${t.id}`, {
      method: 'PUT', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ is_active: t.is_active ? 0 : 1 }),
    });
    await fetchItems();
  };

  const grouped = items.reduce<Record<string, CsTemplate[]>>((acc, t) => {
    (acc[t.category] = acc[t.category] || []).push(t);
    return acc;
  }, {});

  return (
    <AdminLayout currentPage="cs-templates" onLogout={handleLogout}>
      <div className="space-y-6">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h1 className="flex items-center gap-2 text-2xl font-bold text-gray-800 dark:text-white">
              <MessageSquareText size={26} className="text-red-600" /> CS 話術庫
            </h1>
            <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
              客服對答快捷回覆（與 WhatsApp 自動模板分開）——對話區 ⚡ 一撣即出，用得多排得前。
            </p>
          </div>
          <button onClick={openCreate} className="inline-flex items-center gap-1.5 rounded-lg bg-red-600 px-4 py-2 text-sm font-bold text-white hover:bg-red-700">
            <Plus size={16} /> 新增話術
          </button>
        </div>

        {loading ? (
          <div className="rounded-xl border border-gray-200 bg-white p-8 text-center text-gray-500 dark:border-gray-700 dark:bg-gray-800">載入中...</div>
        ) : Object.keys(grouped).length === 0 ? (
          <div className="rounded-xl border border-gray-200 bg-white p-8 text-center text-gray-500 dark:border-gray-700 dark:bg-gray-800">暫無話術</div>
        ) : (
          Object.entries(grouped).map(([cat, list]) => (
            <section key={cat} className="rounded-xl border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800">
              <h2 className="mb-3 text-sm font-bold text-gray-500 dark:text-gray-400">{cat}（{list.length}）</h2>
              <div className="space-y-3">
                {list.map((t) => (
                  <div key={t.id} className={`rounded-lg border p-3 ${t.is_active ? 'border-gray-200 dark:border-gray-700' : 'border-dashed border-gray-300 opacity-60 dark:border-gray-600'}`}>
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <div className="flex items-center gap-2">
                        <span className="font-bold text-gray-800 dark:text-white">{t.title}</span>
                        {!t.is_active && <span className="rounded bg-gray-200 px-1.5 py-0.5 text-[10px] text-gray-600 dark:bg-gray-700 dark:text-gray-300">已停用</span>}
                        {t.use_count > 0 && <span className="text-xs text-gray-400">已用 {t.use_count} 次</span>}
                      </div>
                      <div className="flex items-center gap-1">
                        <button onClick={() => toggleActive(t)} className="rounded-md border border-gray-200 px-2 py-1 text-xs text-gray-600 hover:border-red-400 hover:text-red-600 dark:border-gray-600 dark:text-gray-300">{t.is_active ? '停用' : '啟用'}</button>
                        <button onClick={() => openEdit(t)} title="編輯" className="p-1.5 text-gray-500 hover:text-blue-600"><Pencil size={15} /></button>
                        <button onClick={() => handleDelete(t)} title="刪除" className="p-1.5 text-gray-500 hover:text-red-600"><Trash2 size={15} /></button>
                      </div>
                    </div>
                    <p className="mt-2 whitespace-pre-wrap text-sm text-gray-600 dark:text-gray-300">{t.body}</p>
                  </div>
                ))}
              </div>
            </section>
          ))
        )}
      </div>

      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-lg rounded-xl bg-white shadow-xl dark:bg-gray-800">
            <form onSubmit={handleSave} className="p-6">
              <div className="mb-4 flex items-center justify-between">
                <h2 className="flex items-center gap-2 text-lg font-bold text-gray-900 dark:text-white">
                  {editing ? <Pencil size={18} /> : <Plus size={18} />} {editing ? '編輯話術' : '新增話術'}
                </h2>
                <button type="button" onClick={() => setShowModal(false)} className="text-gray-400 hover:text-gray-600"><X size={20} /></button>
              </div>
              {err && <div className="mb-3 rounded-lg border border-red-200 bg-red-50 p-2.5 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-300">{err}</div>}
              <div className="space-y-3">
                <div>
                  <label className="mb-1 block text-xs font-medium text-gray-500 dark:text-gray-400">標題 *（ picker 顯示用）</label>
                  <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="w-full rounded-lg border border-gray-300 p-2.5 text-sm outline-none focus:border-red-500 dark:border-gray-600 dark:bg-gray-900 dark:text-white" />
                </div>
                <div>
                  <label className="mb-1 block text-xs font-medium text-gray-500 dark:text-gray-400">分類</label>
                  <input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} list="cs-cats" className="w-full rounded-lg border border-gray-300 p-2.5 text-sm outline-none focus:border-red-500 dark:border-gray-600 dark:bg-gray-900 dark:text-white" />
                  <datalist id="cs-cats">
                    {[...new Set(items.map((i) => i.category))].map((c) => <option key={c} value={c} />)}
                  </datalist>
                </div>
                <div>
                  <label className="mb-1 block text-xs font-medium text-gray-500 dark:text-gray-400">內容 *（實際發送文字）</label>
                  <textarea value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} rows={6} className="w-full resize-y rounded-lg border border-gray-300 p-2.5 text-sm outline-none focus:border-red-500 dark:border-gray-600 dark:bg-gray-900 dark:text-white" />
                </div>
              </div>
              <div className="mt-5 flex justify-end gap-3">
                <button type="button" onClick={() => setShowModal(false)} className="rounded-lg border border-gray-300 px-4 py-2 text-sm text-gray-600 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-300">取消</button>
                <button type="submit" disabled={saving} className="flex items-center gap-2 rounded-lg bg-red-600 px-4 py-2 text-sm font-bold text-white hover:bg-red-700 disabled:opacity-50">
                  {saving ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />} 儲存
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

export default AdminCsTemplates;
